<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue';
import useDashboardStore from '@/stores/Dashboard/dashboard-store';
import { DashboardWidget } from '@/interfaces/Dashboard';
import debounce from 'lodash/debounce';
import ConnxSkeletonExpiredLicencesCount from './ConnxSkeletonExpiredLicencesCount.vue';
import ConnxDashboardWidgetNoData from './ConnxDashboardWidgetNoData.vue';
import ConnxStatusCard from '@/components/Atoms/ConnxStatusCard.vue';
import type { Status } from '@/interfaces/Dashboard';

const props = defineProps<{ height: number; type: string; widget: DashboardWidget }>();

const total = ref<number>(0);
const loading = ref(true);

const dashboardStore = useDashboardStore();

const status = computed<Status>(() => {
  const [benchmarkFirst, benchmarkSecond] = props.widget.defaultJsonProperties?.parameters?.benchmarks?.values ?? [
    0, 0,
  ];

  if (total.value <= benchmarkFirst) {
    return 'success';
  }

  if (total.value > benchmarkFirst && total.value <= benchmarkSecond) {
    return 'warning';
  }

  return 'danger';
});

const fetchData = async () => {
  try {
    const data = await dashboardStore.fetchDataSource(props.widget.dataSourceGuid, {
      widgetGuid: props.widget.dashboardTabWidgetGuid,
    });
    if (data?.data) {
      total.value = (data.data.total ?? 0).toFixed(1);
    }
  } catch (error) {
    console.error('Failed to fetch data:', error);
  } finally {
    loading.value = false;
  }
};

const debouncedFetchData = debounce(async () => {
  await fetchData();
}, 600);

const badgeText = computed(() => {
  switch (status.value) {
    case 'success':
      return 'Low Risk Level';
    case 'warning':
      return 'Medium Risk Level';
    case 'danger':
      return 'Critical Risk Level';
    default:
      return 'Risk Level';
  }
});

watch(
  () => {
    const p = props.widget.defaultJsonProperties?.parameters;
    return {
      filterByValues: p?.filterBy?.map((f) => f.value) ?? [],
      benchmarkValues: p?.benchmarks?.values,
      groupByValue: p?.groupBy?.value ?? null,
      orderByValue: p?.orderBy?.value ?? null,
      previousMonthsValue: p?.previousMonths?.value,
      previousRangeSelected: p?.previousRange?.selected ?? false,
      previousRangeFrom: p?.previousRange?.value[0] ?? '',
      previousRangeTo: p?.previousRange?.value[1] ?? '',
    };
  },
  async () => {
    await debouncedFetchData();
  },
  { deep: false }
);

onMounted(fetchData);
</script>

<template>
  <ConnxSkeletonExpiredLicencesCount v-if="loading" />

  <div v-else class="tw-w-full tw-h-auto tw-p-0 tw-pt-2 tw-flex tw-flex-col">
    <!-- Big Card -->
    <ConnxStatusCard :title="`${total}%`" :status="status" :badge-text="badgeText" />
  </div>
</template>
